import React from 'react';
import { AiOutlineTrophy } from 'react-icons/ai';
import { EmptyState } from '@/components/custom/empty-state';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export const PLAYFUL_STAT_BG = ['bg-[#FFE0C2]', 'bg-[#CAFDF5]', 'bg-[#D3FCD2]', 'bg-[#D4C4E8]'];

const VISIBLE_CLASSROOMS_COUNT = 2;

function ClassroomsCell({ classrooms, playfulTeacher }) {
  const visible = classrooms.slice(0, VISIBLE_CLASSROOMS_COUNT);
  const extra = classrooms.slice(VISIBLE_CLASSROOMS_COUNT);

  return (
    <div className="flex flex-wrap items-center gap-1">
      <p
        className="truncate text-sm"
        title={visible.join(', ')}
      >
        {visible.join(', ') || '-'}
      </p>
      {extra.length > 0 && (
        <Badge
          variant={playfulTeacher ? 'muted' : 'default'}
          size="sm"
          title={extra.join('\n')}
        >
          {`+${extra.length}`}
        </Badge>
      )}
    </div>
  );
}

export function LiveChallengeResults({ playfulTeacher, statCards, search, onSearchChange, ranking = [], emptyText = 'Nenhum aluno encontrado' }) {
  return (
    <div className="flex min-w-0 flex-col gap-5">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card, index) => (
          <div
            key={index}
            className={cn('rounded-xl px-4 py-3 text-center', playfulTeacher ? PLAYFUL_STAT_BG[index] : 'bg-primary-lightest')}
          >
            <p className="truncate font-bold text-primary-dark">{card.primary}</p>
            <p className="text-sm text-primary-dark">{card.secondary}</p>
          </div>
        ))}
      </div>

      <div className="report-widget__filter min-w-0">
        <div className="relative w-full min-w-0">
          <input
            className="report-form__control !w-full outline-none sm:!w-[400px]"
            aria-label="Pesquisar"
            placeholder="Pesquisar"
            type="search"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
          />
        </div>
      </div>

      {ranking.length === 0 ? (
        <EmptyState
          type="data"
          variant="plain"
          text={emptyText}
        />
      ) : (
        <div className="round-dg-wrapper max-h-[420px] min-w-0 overflow-y-auto">
          <table className="round-dg round-dg--light min-w-[720px]">
            <thead className="round-dg__header">
              <tr className="round-dg__row">
                <th className="round-dg__cell-header round-dg__cell-header--sm w-24">Posição</th>
                <th className="round-dg__cell-header round-dg__cell-header--sm w-56">Aluno/Email</th>
                <th className="round-dg__cell-header round-dg__cell-header--sm w-72">Instituição/Curso</th>
                <th className="round-dg__cell-header round-dg__cell-header--sm w-72">Turma</th>
                <th className="round-dg__cell-header round-dg__cell-header--sm w-32 text-right">Pontuação</th>
              </tr>
            </thead>
            <tbody className="round-dg__body">
              {ranking.map((student, index) => (
                <tr
                  key={student.id ?? `${student.name}-${index}`}
                  className="round-dg__row"
                >
                  <td className="round-dg__cell">
                    <span className="mr-4 text-sm font-semibold text-primary">{index + 1}</span>
                    {index === 0 && <AiOutlineTrophy className="card__icon card__icon--golden" />}
                    {index === 1 && <AiOutlineTrophy className="card__icon card__icon--silver" />}
                    {index === 2 && <AiOutlineTrophy className="card__icon card__icon--bronze" />}
                  </td>
                  <th className="round-dg__cell-header round-dg__cell-header--row">
                    <p
                      className="truncate text-sm font-semibold text-primary-dark"
                      title={student.name}
                    >
                      {student.name}
                    </p>
                    {student.email && (
                      <p
                        className="truncate text-neutral"
                        title={student.email}
                      >
                        {student.email}
                      </p>
                    )}
                  </th>
                  <td className="round-dg__cell round-dg__cell--sm">
                    <p
                      className="truncate text-sm"
                      title={student.institutionCourse}
                    >
                      {student.institutionCourse || '-'}
                    </p>
                  </td>
                  <td className="round-dg__cell round-dg__cell--sm">
                    {Array.isArray(student.classrooms) ? (
                      <ClassroomsCell
                        classrooms={student.classrooms}
                        playfulTeacher={playfulTeacher}
                      />
                    ) : (
                      <p
                        className="truncate text-sm"
                        title={student.classroom}
                      >
                        {student.classroom || '-'}
                      </p>
                    )}
                  </td>
                  <td
                    className={cn(
                      'round-dg__cell round-dg__cell--sm text-right !text-base font-bold',
                      index === 0 && 'text-warm',
                      index === 1 && 'text-[#5D6A86]',
                      index === 2 && 'text-[#D3681A]',
                      index > 2 && '!text-sm !font-normal !text-neutral-darker'
                    )}
                  >
                    {student.points} pts
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
